import { publish } from 'pubsub-js';
import { noop } from 'lodash';
import { config } from 'config';

import {
  deleteHasActiveAuthConversation,
  saveHasActiveAuthConversation,
} from 'active-auth-conversation/active-auth-conversation';
import { isLoggedInUser } from 'utils/isLoggedInUser';
import { PubSubEvent } from 'genesys/pub-sub-events';

import {
  MessagingServiceEvent,
  MessagingServiceCommand,
} from './messaging-service-plugin';
import { AuthCommand, AuthEvent } from './auth-plugin';
import { CobrowseServiceCommand } from './cobrowse-service-plugin';
import { clearActiveChatStorage, clearAllStorage } from './genesys.storage';

const COMMAND = `command`;
const SUBSCRIBE = `subscribe`;
const DATABASE_SET = 'Database.set';
const DATABASE_UPDATE = 'Database.update';
export const CHAT_BOT = 'chatbot';
export const CHATBOT_HISTORY = `**Chatbot history**`;

export const EVENTS = {
  DISCONNECT: 'Disconnect',
};

const getDeploymentId = () =>
  window.utag_data?.vendor_genesys_messaging_deploymentid;

const hasActiveConversation = () => {
  const deploymentId = getDeploymentId();

  if (!deploymentId) {
    return false;
  }

  const activeConversation = JSON.parse(
    localStorage.getItem(`_${deploymentId}:gcmcsessionActive`),
  )?.value;

  return activeConversation === 'true';
};

const genesysDatabaseUpdate = (values, callback) => {
  window.Genesys(
    COMMAND,
    DATABASE_UPDATE,
    {
      messaging: {
        customAttributes: {
          ...values,
        },
      },
    },
    callback,
    callback,
  );
};

const genesysDatabaseSet = (values) => {
  window.Genesys(COMMAND, DATABASE_SET, {
    messaging: {
      customAttributes: {
        ...values,
      },
    },
  });
};

export const updateDatabaseThenSendInitialMessages = (
  { chatHistoryLength, hasChatWithAgentBtnClicked },
  sendInitialMessages,
) => {
  genesysDatabaseUpdate(
    {
      History_Length: chatHistoryLength,
      Button_Clicked: hasChatWithAgentBtnClicked,
    },
    sendInitialMessages,
  );
};

const genesysDataReset = () => {
  genesysDatabaseSet({
    TECH_Intent: window.utag_data?.vendor_genesys_messaging_intent,
    Page_URL: window.location.href,
    User_Agent: window.navigator.userAgent,
  });
};

/**
 * Clear an exsiting conversation
 */
export const clearConversation = ({ retry, clearStorage } = {}) => {
  const isUserLoggedIn = isLoggedInUser();

  window.Genesys(
    COMMAND,
    MessagingServiceCommand.ClearConversation,
    {},
    () => {
      genesysDataReset();
      !!retry && retry();

      if (!isUserLoggedIn && !retry) {
        publish(AuthEvent.LoggedOut);
      }
    },
    () => {
      clearActiveChatStorage();
      (!!retry && retry()) ||
        publish(MessagingServiceEvent.ConversationCleared);

      if (!isUserLoggedIn && !retry) {
        publish(AuthEvent.LoggedOut);
      }
    },
  );

  if (!!clearStorage && !!isUserLoggedIn) {
    deleteHasActiveAuthConversation();
  }
};

/**
 * Start a new conversation
 */
export const startConversation = async ({
  retry = 0,
  onSuccess,
  onFailure,
}) => {
  const startConversationCommand = (tryCount) => {
    window.Genesys(
      COMMAND,
      MessagingServiceCommand.StartConversation,
      {},
      () => {
        onSuccess?.();
      },
      () => {
        if (tryCount < retry) {
          clearConversation({
            retry: () => {
              startConversationCommand(tryCount + 1);
            },
          });
        } else {
          onFailure?.();
        }
      },
    );
  };

  if (!hasActiveConversation()) {
    startConversationCommand(1);
  } else {
    clearConversation({
      retry: () => {
        startConversationCommand(1);
      },
    });
  }
};

/**
 * Accept co browse offering
 */
export const acceptCoBrowseOffering = ({ sessionJoinToken, sessionId }) => {
  window.Genesys(COMMAND, CobrowseServiceCommand.AcceptSession, {
    joinCode: sessionJoinToken,
    sessionId,
  });
};

/**
 * Decline co-browse offering
 */
export const declineCoBrowseOffering = ({ sessionJoinToken, sessionId }) => {
  window.Genesys(COMMAND, CobrowseServiceCommand.DeclineSession, {
    joinCode: sessionJoinToken,
    sessionId,
  });
};

/**
 * restore genesys apiEndpoint config to avoid
 * invalid refresh call from Genesys.
 */
export const restoreGenesysConfig = () => {
  const deploymentId = getDeploymentId();

  clearInterval(window.authSettingsReset);
  window.authSettingsReset = setInterval(function () {
    window.Genesys(COMMAND, AuthCommand.Configure, {
      deploymentConfig: {
        apiEndpoint: 'https://api.euw2.pure.cloud',
      },
      snippetConfig: {
        deploymentId,
      },
    });
  }, 2000);
};

/**
 * Logout user on application logout
 */
export const chatLogout = (isEndChat) => {
  if (!isEndChat) {
    document.cookie = 'vaOpened=; Max-Age=-1;';
    clearAllStorage();

    return;
  }

  window.Genesys(COMMAND, AuthCommand.Logout, {}, () => {
    document.cookie = 'vaOpened=; Max-Age=-1;';
  });
};

export const checkDeploymentIdStatus = () =>
  new Promise((resolve) => {
    window.Genesys(COMMAND, 'GenesysJS.configuration', function (config) {
      resolve(config.status === 'Active');
    });
  });

/**
 * Send chat history to agent
 * ISSUE: messages are not sent in expected order
 * @param {chatBotChat} chatBotChat
 * @returns void
 */
const sendChatHistory = ({ defaultAnswer, question, by }) => {
  const stripHtml = () => {
    const answerHTML = new DOMParser().parseFromString(
      defaultAnswer,
      'text/html',
    );

    return answerHTML.body.textContent;
  };

  if (by !== CHAT_BOT) {
    return;
  }

  const NEW_LINE = `\n`;

  if (question) {
    sendMessage({ message: `${CHATBOT_HISTORY + NEW_LINE + question}` });
  }

  if (defaultAnswer) {
    sendMessage({
      message: `${CHATBOT_HISTORY + NEW_LINE + stripHtml()}`,
    });
  }
};

/**
 * Send message to the agent
 * TODO: look into chat history
 * @param {message} message
 */
export const sendMessage = ({ chatHistory = [], message }) => {
  chatHistory.forEach(sendChatHistory);

  window.Genesys(COMMAND, MessagingServiceCommand.SendMessage, { message });
};

/**
 * Genesys subscription events.
 */
export const genesysSubcribeEvents = () => {
  const _Genesys = window.Genesys || noop;

  const handleMessagingReady = () => {
    // fetch history
    if (config.isDev) {
      genesysDatabaseUpdate({
        TECH_Intent: window.utag_data?.vendor_genesys_messaging_intent,
        Page_URL: window.location.href,
        User_Agent: window.navigator.userAgent,
      });
    }

    publish(PubSubEvent.MessageServiceReady);
  };

  const restoreConversation = (lastMessage) => {
    if (!lastMessage) {
      publish(MessagingServiceEvent.Restored);

      return false;
    }

    if (lastMessage?.presence?.type === EVENTS.DISCONNECT) {
      clearConversation({ clearStorage: true });

      return false;
    } else {
      publish(MessagingServiceEvent.Restored);

      return true;
    }
  };

  const handleMessagingRestored = ({ data: { messages } }) => {
    const [lastMessage] = messages;
    const hasConversationRestored = restoreConversation(lastMessage);

    if (!!hasConversationRestored) {
      publish(PubSubEvent.MessageRestored, messages.reverse());
    }
  };

  const handleConversationCleared = () => {
    publish(MessagingServiceEvent.ConversationCleared);
  };

  const handleMessageRecieved = ({ data }) => {
    publish(MessagingServiceEvent.MessagesReceived, data);
  };

  _Genesys(SUBSCRIBE, MessagingServiceEvent.Error, ({ data }) => {
    publish(MessagingServiceEvent.Error, data);
  });

  // On Messaging service ready on the application
  _Genesys(SUBSCRIBE, MessagingServiceEvent.Ready, handleMessagingReady);
  // On customer refresh or navigate to another page
  _Genesys(SUBSCRIBE, MessagingServiceEvent.Restored, handleMessagingRestored);
  // On customer end conversation
  _Genesys(
    SUBSCRIBE,
    MessagingServiceEvent.ConversationCleared,
    handleConversationCleared,
  );
  _Genesys(
    SUBSCRIBE,
    MessagingServiceEvent.MessagesReceived,
    handleMessageRecieved,
  );
  _Genesys(SUBSCRIBE, MessagingServiceEvent.ConversationDisconnected, () =>
    clearConversation({ clearStorage: true }),
  );

  const handleAuthServiceError = (error) => publish(AuthEvent.Error, error);

  _Genesys(SUBSCRIBE, AuthEvent.AuthError, handleAuthServiceError);

  _Genesys(SUBSCRIBE, AuthEvent.Error, handleAuthServiceError);

  _Genesys(SUBSCRIBE, MessagingServiceEvent.RestoreFailed, ({ data }) => {
    const { code } = data;
    const SUCCESS = 200;

    if (code !== SUCCESS) {
      publish(MessagingServiceEvent.RestoreFailed);
    }
  });

  [
    AuthEvent.LoggedOut,
    AuthEvent.Authenticated,
    AuthEvent.Authenticating,
    AuthEvent.AuthError,
    AuthEvent.AuthProviderError,
    AuthEvent.Error,
    AuthEvent.TokenError,
  ].forEach((authEvent) => {
    _Genesys(SUBSCRIBE, authEvent, ({ data }) => {
      publish(authEvent, data);
    });
  });

  _Genesys(SUBSCRIBE, MessagingServiceEvent.Started, (data) => {
    if (isLoggedInUser()) {
      saveHasActiveAuthConversation();
    }

    publish(MessagingServiceEvent.Started, data);
  });
};
